import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { billingMonthOptions, currentBillingMonth, getMonthlyBillPreview, previewErrorMessage } from "./billingPreviewService";
import "./monthlyBillPreview.css";
import NeoSiteUsage from "./NeoSiteUsage";

const money = (cents) => cents == null ? "Needs review" : new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 2 }).format(cents / 100);
const dateLabel = (value, time = false) => value ? new Intl.DateTimeFormat("en-ZA", { timeZone: "Africa/Johannesburg", dateStyle: "medium", ...(time ? { timeStyle: "short" } : {}) }).format(new Date(value.length === 10 ? `${value}T12:00:00+02:00` : value)) : "—";
function themeAt(element) {
  if (element?.closest(".super-admin-root")) return "dark";
  if (document.documentElement.dataset.theme === "light" || document.body.dataset.theme === "light" || document.body.classList.contains("mc-theme-light")) return "light";
  const background = getComputedStyle(element).getPropertyValue("--brand-bg").trim();
  const match = /^#([a-f\d]{6})$/i.exec(background);
  if (match) {
    const n = parseInt(match[1], 16);
    if (((n >> 16) * 299 + ((n >> 8) & 255) * 587 + (n & 255) * 114) / 1000 > 160) return "light";
  }
  return "dark";
}

function PreviewDialog({ orgId, orgName, theme, onClose, currentPreview, onPreviewChange }) {
  const titleId = useId();
  const dialog = useRef(null);
  const requestId = useRef(0);
  const savedPreview = useRef(currentPreview);
  const [month, setMonth] = useState(currentBillingMonth);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const id = ++requestId.current;
    let active = true;
    const saved = savedPreview.current;
    // The page already loaded this month. Reuse that exact result instead of a second read.
    if (refresh === 0 && saved?.orgId === orgId && saved?.period?.periodKey === month) {
      setPreview(saved); setBusy(false); setError("");
      return () => { active = false; };
    }
    setBusy(true); setPreview(null); setError("");
    getMonthlyBillPreview(orgId, month, refresh > 0).then((data) => {
      if (active && id === requestId.current) {
        setPreview(data);
        if (data.period.periodKey === currentBillingMonth()) savedPreview.current = data;
        onPreviewChange?.(data);
      }
    }).catch((failure) => {
      if (active && id === requestId.current) setError(previewErrorMessage(failure));
    }).finally(() => {
      if (active && id === requestId.current) setBusy(false);
    });
    return () => { active = false; };
  }, [orgId, month, refresh, onPreviewChange]);
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.querySelector("button")?.focus();
    const handleKey = (event) => {
      if (event.key === "Escape") { event.preventDefault(); onClose(); }
      if (event.key !== "Tab") return;
      const elements = [...dialog.current.querySelectorAll("button:not(:disabled), select:not(:disabled), summary, a[href], [tabindex='0']")].filter((el) => el.getClientRects().length);
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialog.current.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !dialog.current.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", handleKey);
    return () => { document.body.style.overflow = overflow; document.removeEventListener("keydown", handleKey); previous?.focus?.(); };
  }, [onClose]);

  const bundled = preview?.pricingMode === "enterprise_bundle";
  const mcRows = preview?.mc.rows.filter((row) => row.countStarts > 0) || [];
  const unused = preview?.mc.rows.filter((row) => !row.countStarts) || [];
  return createPortal(
    <div className="matrixos-bill-overlay" data-theme={theme} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section ref={dialog} className="matrixos-bill-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-busy={busy}>
        <header className="matrixos-bill-header">
          <div><p className="matrixos-bill-eyebrow">Billing & usage</p><h2 id={titleId}>Monthly bill preview</h2><p>{preview?.orgName || orgName || "Your company"}</p></div>
          <button type="button" className="matrixos-bill-close" onClick={onClose} aria-label="Close monthly bill preview">×</button>
        </header>
        <div className="matrixos-bill-controls">
          <label>Usage month<select value={month} onChange={(event) => { setRefresh(0); setMonth(event.target.value); }}>{billingMonthOptions().map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          <button type="button" className="matrixos-bill-button" disabled={busy} onClick={() => setRefresh((value) => value + 1)}>Refresh</button>
        </div>
        {busy && <p className="matrixos-bill-state" role="status">Loading this month’s usage…</p>}
        {error && <p className="matrixos-bill-notice" role="alert">{error}</p>}
        {preview && <>
          <div className="matrixos-bill-totals">
            <article><span>Neo</span><strong>{bundled ? "Included" : money(preview.neo.amountCents)}</strong><small>{preview.neo.sites} site{preview.neo.sites === 1 ? "" : "s"} · {preview.neo.activeStaff} active staff{preview.neo.trial ? " · Trial" : ""}</small></article>
            <article><span>MC</span><strong>{bundled ? "Included" : money(preview.mc.amountCents)}</strong><small>{mcRows.length} site{mcRows.length === 1 ? "" : "s"} started counts{preview.mc.trial ? " · Trial" : ""}</small></article>
            <article className="matrixos-bill-total"><span>{preview.period.closed ? "Month total" : "Month so far"}</span><strong>{preview.sourceComplete === false ? "Needs review" : money(preview.totalCents)}</strong><small>{bundled ? "Enterprise plan · " : ""}Due {dateLabel(preview.paymentDueDate || preview.period.dueDate)}</small></article>
          </div>
          <p className="matrixos-bill-explainer">MC charges a site once for the month when it starts a real count. Finishing or restoring an older count adds no new fee.</p>
          <details className="matrixos-bill-breakdown" open>
            <summary>MC site breakdown <span>{preview.mc.countStarts} counts started</span></summary>
            {mcRows.length ? <div className="matrixos-bill-table-scroll"><table><thead><tr><th>Site</th><th>Counts started</th><th>Monthly fee</th></tr></thead><tbody>{mcRows.map((row) => <tr key={row.siteId}><td><strong>{row.siteName}</strong><small>First start {dateLabel(row.firstStartedAt)}{row.withNeo ? " · With Neo" : ""}{row.trial ? " · Trial" : ""}{!row.billable ? " · Exempt" : ""}</small></td><td>{row.countStarts}</td><td>{bundled ? "Included" : money(row.amountCents)}</td></tr>)}</tbody></table></div> : <p className="matrixos-bill-empty">No sites started a real count this month. MC usage is {money(0)}.</p>}
            {unused.length > 0 && <details className="matrixos-bill-unused"><summary>{unused.length} site{unused.length === 1 ? "" : "s"} with no new count · {money(0)} usage</summary><p>{unused.map((row) => row.siteName).join(" · ")}</p></details>}
          </details>
          <details className="matrixos-bill-breakdown">
            <summary>Neo site breakdown <span>{preview.neo.clockIns} clock-ins</span></summary>
            <p className="matrixos-bill-explainer">Each site uses the higher of {money(preview.rates.neoMinimumCents)} or active staff × {money(preview.rates.neoStaffCents)}. Sites without active staff have {money(0)} usage.</p>
            <NeoSiteUsage neo={preview.neo} fee={(value) => bundled ? "Included" : money(value)} />
          </details>
          {(preview.reviewRequired || preview.startsPending) && <details className="matrixos-bill-notice"><summary>Checks before issuing a bill</summary><ul>{[...new Set([...preview.warnings, ...(preview.startsPending ? ["Some new count starts are waiting to be confirmed. Refresh after the connection recovers."] : [])])].map((warning) => <li key={warning}>{warning}</li>)}</ul></details>}
          <p className="matrixos-bill-caption">Updated {dateLabel(preview.generatedAt, true)}{preview.cache?.reused ? " · Saved preview" : ""}</p>
        </>}
        <footer className="matrixos-bill-footer"><p>Preview only. Payments and earlier unpaid bills stay separate.</p><span>MatrixOS | Operate with Clarity.</span></footer>
      </section>
    </div>, document.body,
  );
}

export default function MonthlyBillPreview({ orgId, orgName, currentPreview, onPreviewChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState("dark");
  const close = React.useCallback(() => setOpen(false), []);
  useEffect(() => { setOpen(false); }, [orgId]);
  return <>
    <button type="button" className="matrixos-bill-button" disabled={disabled || !orgId} onClick={(event) => { setTheme(themeAt(event.currentTarget)); setOpen(true); }}>Monthly bill preview</button>
    {open && <PreviewDialog orgId={orgId} orgName={orgName} theme={theme} onClose={close} currentPreview={currentPreview} onPreviewChange={onPreviewChange} />}
  </>;
}
